/**
 * ErrorState
 * Displays a user-friendly error message with an optional retry action.
 *
 * Props:
 *   message – human-readable error text
 *   onRetry – optional callback to retry the failed operation
 *   dark    – use light text on dark background (default: false)
 */

import React from 'react';

interface ErrorStateProps {
  message?: string;
  onRetry?: () => void;
  dark?: boolean;
}

const ErrorState: React.FC<ErrorStateProps> = ({
  message = 'Something went wrong. Please try again.',
  onRetry,
  dark = false,
}) => {
  const textClass = dark ? 'text-gray-300' : 'text-gray-600';
  const subTextClass = dark ? 'text-gray-400' : 'text-gray-500';

  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
      <div className="text-5xl">⚠️</div>
      <p className={`font-semibold text-lg ${textClass}`}>Error</p>
      <p className={`text-sm max-w-md ${subTextClass}`}>{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          Try Again
        </button>
      )}
    </div>
  );
};

export default ErrorState;
